<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import SettingsLayout from 'dashboard/components-next/Settings/SettingsLayout.vue';
import IntegrationEmptyState from '../shared/IntegrationEmptyState.vue';
import OAuthApplicationItem from './OAuthApplicationItem.vue';
import NewOAuthApp from './NewOAuthApp.vue';
import EditOAuthApp from './EditOAuthApp.vue';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const showNewAppModal = ref(false);
const showEditAppModal = ref(false);
const editingApplication = ref(null);

const applications = computed(
  () => getters['oauthApplications/getOAuthApplications'].value || []
);
const uiFlags = computed(
  () => getters['oauthApplications/getUIFlags'].value || {}
);
const isLoading = computed(() => uiFlags.value.isFetching);
const isEmpty = computed(() => !applications.value.length);

const openNewModal = () => {
  showNewAppModal.value = true;
};

const hideNewModal = () => {
  showNewAppModal.value = false;
};

const openEditModal = application => {
  editingApplication.value = application;
  showEditAppModal.value = true;
};

const hideEditModal = () => {
  showEditAppModal.value = false;
  editingApplication.value = null;
};

const handleCreate = async appData => {
  try {
    await store.dispatch('oauthApplications/create', appData);
    useAlert(t('OAUTH_APPLICATIONS.CREATE.API_SUCCESS'));
    hideNewModal();
  } catch (error) {
    useAlert(t('OAUTH_APPLICATIONS.CREATE.API_ERROR'));
  }
};

const handleDelete = async id => {
  try {
    await store.dispatch('oauthApplications/delete', id);
    useAlert(t('OAUTH_APPLICATIONS.DELETE.API_SUCCESS'));
  } catch (error) {
    useAlert(t('OAUTH_APPLICATIONS.DELETE.API_ERROR'));
  }
};

const handleUpdate = async appData => {
  try {
    await store.dispatch('oauthApplications/update', appData);
    useAlert(t('OAUTH_APPLICATIONS.UPDATE.API_SUCCESS'));
    hideEditModal();
  } catch (error) {
    useAlert(t('OAUTH_APPLICATIONS.UPDATE.API_ERROR'));
  }
};

const handleRegenerateSecret = async id => {
  try {
    await store.dispatch('oauthApplications/regenerateSecret', id);
    useAlert(t('OAUTH_APPLICATIONS.UPDATE.SECRET_REGENERATED'));
    hideEditModal();
  } catch (error) {
    useAlert(t('OAUTH_APPLICATIONS.UPDATE.SECRET_REGENERATE_ERROR'));
  }
};

onMounted(() => {
  store.dispatch('oauthApplications/get').catch(() => {
    useAlert(t('OAUTH_APPLICATIONS.API.ERROR_MESSAGE'));
  });
});
</script>

<template>
  <SettingsLayout
    :header-title="$t('OAUTH_APPLICATIONS.HEADER')"
    :button-label="$t('OAUTH_APPLICATIONS.NEW_APP')"
    @click="openNewModal"
  >
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <Spinner />
    </div>

    <IntegrationEmptyState
      v-else-if="isEmpty"
      icon="i-lucide-key-round"
      :title="$t('OAUTH_APPLICATIONS.LIST.404')"
      :description="$t('OAUTH_APPLICATIONS.DESCRIPTION')"
      :button-label="$t('OAUTH_APPLICATIONS.NEW_APP')"
      button-icon="i-lucide-plus"
      @action="openNewModal"
    />

    <div v-else class="flex flex-col gap-4">
      <p class="text-sm text-n-slate-11">
        {{ $t('OAUTH_APPLICATIONS.DESCRIPTION') }}
      </p>
      <OAuthApplicationItem
        v-for="app in applications"
        :key="app.id"
        :application="app"
        @delete="handleDelete"
        @edit="openEditModal"
      />
    </div>

    <woot-modal v-model:show="showNewAppModal" :on-close="hideNewModal">
      <NewOAuthApp
        v-if="showNewAppModal"
        :is-submitting="uiFlags.isCreating"
        @close="hideNewModal"
        @create="handleCreate"
      />
    </woot-modal>

    <woot-modal v-model:show="showEditAppModal" :on-close="hideEditModal">
      <EditOAuthApp
        v-if="showEditAppModal && editingApplication"
        :application="editingApplication"
        :is-submitting="uiFlags.isUpdating"
        @close="hideEditModal"
        @update="handleUpdate"
        @regenerate-secret="handleRegenerateSecret"
      />
    </woot-modal>
  </SettingsLayout>
</template>
